import { useCallback, useEffect, useRef, useState } from "react";
import { useReports } from "../../../hooks/useReports";
import {
  ReportShare,
  ReportShareCandidate,
  ReportSharesResponse,
  SalonReport,
} from "../../../types/reports";
import { notifyError, notifySuccess } from "../../../utils/notify";

export interface ReportShares {
  shares: ReportShare[];
  candidates: ReportShareCandidate[];
  /** First load for the open report (later refreshes are silent). */
  loading: boolean;
  loadError: string | null;
  /** Share with these assignments. Resolves true on success. */
  share: (recipientIds: string[], syncWithSource: boolean) => Promise<boolean>;
  setSync: (share: ReportShare, syncWithSource: boolean) => Promise<void>;
  revoke: (share: ReportShare) => Promise<void>;
  /** The share whose sync / revoke request is in flight. */
  busyShareId: string | null;
}

const RETRY = "Try again in a moment.";

/**
 * Data for the Share modal: who has a copy of `report` and who else it can
 * go to, plus share / keep-in-sync / revoke with their toasts.
 * `onShareCountChange` reports the new number of copies after a share or
 * revoke, so the shell's list (and the Remove dialog) stay current.
 * Pass a null report while the modal is closed; nothing loads then.
 *
 * Only the latest request's answer is applied, so closing the modal or
 * switching reports mid-request never shows another report's shares. After
 * an action the list refreshes silently (no spinner over the list).
 */
export function useReportShares(
  salonId: string,
  report: SalonReport | null,
  onShareCountChange: (report: SalonReport, count: number) => void,
): ReportShares {
  const {
    listReportShares,
    createReportShares,
    setReportShareSync,
    revokeReportShare,
  } = useReports();
  const [shares, setShares] = useState<ReportShare[]>([]);
  const [candidates, setCandidates] = useState<ReportShareCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyShareId, setBusyShareId] = useState<string | null>(null);
  const latestRequest = useRef(0);
  const reportId = report?.id ?? null;

  /** Fetch shares + candidates; resolves to the response it applied, or
   *  null when it failed or a newer request superseded it. */
  const load = useCallback(
    async ({ silent = false } = {}): Promise<ReportSharesResponse | null> => {
      const request = ++latestRequest.current;
      if (!reportId) return null;
      if (!silent) setLoading(true);
      setLoadError(null);
      try {
        const resp = await listReportShares(salonId, reportId);
        if (request !== latestRequest.current) return null;
        setShares(resp.shares);
        setCandidates(resp.candidates);
        return resp;
      } catch (e) {
        if (request !== latestRequest.current) return null;
        console.error("Failed to load report shares:", e);
        setLoadError("Couldn't load who this report is shared with.");
        return null;
      } finally {
        if (request === latestRequest.current) setLoading(false);
      }
    },
    [salonId, reportId, listReportShares],
  );

  // A new (or no) report: drop the previous report's data, then load. Bumping
  // the request id here also discards any answer still on its way.
  useEffect(() => {
    setShares([]);
    setCandidates([]);
    void load();
  }, [load]);

  const share = useCallback(
    async (recipientIds: string[], syncWithSource: boolean) => {
      if (!report) return false;
      try {
        const resp = await createReportShares(salonId, report.id, {
          recipient_assignment_ids: recipientIds,
          sync_with_source: syncWithSource,
        });
        setShares(resp.shares);
        onShareCountChange(report, resp.shares.length);
        notifySuccess("A copy is now in their reports list.", {
          title:
            recipientIds.length === 1
              ? "Report shared"
              : `Report shared with ${recipientIds.length} people`,
        });
        // Candidates changed too. Not awaited: the modal closes on success,
        // and a closed modal drops the answer (see latestRequest).
        void load({ silent: true });
        return true;
      } catch (e) {
        console.error("Failed to share report:", e);
        notifyError(RETRY, { title: "Couldn't share report" });
        await load({ silent: true }); // someone may have been shared with meanwhile
        return false;
      }
    },
    [salonId, report, createReportShares, onShareCountChange, load],
  );

  const setSync = useCallback(
    async (target: ReportShare, syncWithSource: boolean) => {
      if (!report) return;
      setBusyShareId(target.id);
      try {
        const { share: updated } = await setReportShareSync(
          salonId,
          report.id,
          target.id,
          syncWithSource,
        );
        setShares((prev) =>
          prev.map((s) => (s.id === updated.id ? updated : s)),
        );
        notifySuccess(
          syncWithSource
            ? "Their copy now follows your saved view."
            : "Your changes won't reach their copy.",
          {
            title: syncWithSource
              ? `Syncing with ${target.recipient.name}`
              : `Stopped syncing with ${target.recipient.name}`,
          },
        );
      } catch (e) {
        console.error("Failed to change report share sync:", e);
        notifyError(RETRY, { title: "Couldn't change syncing" });
      } finally {
        setBusyShareId(null);
      }
    },
    [salonId, report, setReportShareSync],
  );

  const revoke = useCallback(
    async (target: ReportShare) => {
      if (!report) return;
      setBusyShareId(target.id);
      try {
        await revokeReportShare(salonId, report.id, target.id);
        setShares((prev) => prev.filter((s) => s.id !== target.id));
        notifySuccess("Their copy was removed.", {
          title: `Revoked ${target.recipient.name}'s access`,
        });
        // The refetch is the source of truth for the count (and lets them
        // be picked again).
        const fresh = await load({ silent: true });
        if (fresh) onShareCountChange(report, fresh.shares.length);
      } catch (e) {
        console.error("Failed to revoke report share:", e);
        notifyError(RETRY, { title: "Couldn't revoke access" });
      } finally {
        setBusyShareId(null);
      }
    },
    [salonId, report, revokeReportShare, onShareCountChange, load],
  );

  return {
    shares,
    candidates,
    loading,
    loadError,
    share,
    setSync,
    revoke,
    busyShareId,
  };
}
